import {
  GlobalOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  theme,
  Typography,
  type TableProps,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useApiKeys } from '@client/hooks/useApiKeys';
import { useAuth } from '@client/hooks/useAuth';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useTable } from '@client/hooks/useTable';
import { columnsMetadata } from '@client/pages/tenants/tenant/metadata/apiKeyColumns.metadata';
import styles from '@client/pages/tenants/tenants.module.less';
import { useDirty } from '@client/store/dirty.store';
import { useLocale } from '@client/store/locale.store';
import { requiredField } from '@client/utils/form.util';
import { flexLayout } from '@client/utils/layout.util';
import { isValidOrigin } from '@client/utils/origin.util';
import { indexable } from '@client/utils/table/table.util';

import type { TColumns } from '@client/types';
import type { IApiKey, ISubscription } from '@common/types';

interface ITenantApiProps {
  disabled?: boolean;
  loading?: boolean;
  refetchSubscriptions: () => void;
  subscriptions: ISubscription[];
}

export const TenantApi = (props: ITenantApiProps) => {
  const { disabled, subscriptions, refetchSubscriptions, loading } = props;

  const { ability } = useAuth();
  const isUnlimited = ability.can(EAbilityActions.MANAGE, EAbilitySubjects.ALL);
  const { setDirty } = useDirty();
  const { locale } = useLocale();
  const {
    token: { colorTextTertiary },
  } = theme.useToken();
  const { t } = useTranslation();

  const {
    apiKeys,
    onDeactivateApiKey,
    onActivateApiKey,
    onDeleteApiKey,
    refetch,
  } = useApiKeys();

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<IApiKey>(apiKeys?.data || [], apiKeys?.data?.length || 0);

  const columns: TableProps<IApiKey>['columns'] = columnsMetadata({
    filteredInfo,
    sortedInfo,
    locale,
    disabled,
    entities: apiKeys?.data || [],
    onDelete: onDeleteApiKey,
    onActivateApiKey,
    onDeactivateApiKey,
  });

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle<IApiKey>(columns as TColumns<IApiKey>[], ['createdAt']);

  const tableProps = {
    columns: filteredColumns as TColumns<IApiKey>[],
    pagination: {
      ...pagination,
    },
    scroll: { x: 900 },
    bordered: true,
    size: 'small' as const,
    className: styles.gridList,
    dataSource: indexable<IApiKey>(apiKeys?.data || []),
    loading,
    rowKey: (record: IApiKey) => record.id ?? `row-${Math.random()}`,
    onChange: handleTableChange,
    title: () => (
      <div className={styles.gridHeader}>
        <Toolbar<IApiKey[]>
          onRefresh={refetch}
          entities={apiKeys?.data || []}
          subject={EAbilitySubjects.TENANTS}
        >
          <HideColumns
            columnsList={columnsList}
            selectedColumns={selectedColumns}
            onChange={setSelectedColumns}
          />
        </Toolbar>
      </div>
    ),
    footer: () => (
      <div className={styles.gridFooter}>
        {t('table.total', { amount: apiKeys?.data?.length || 0 })}
      </div>
    ),
  };

  return (
    <>
      <Row gutter={[48, 0]}>
        <Col {...flexLayout.halfColumn}>
          <Form.Item
            name="allowedOrigins"
            label={t('tenant.api.origins')}
            tooltip={t('tenant.api.origins.helper')}
            rules={[
              {
                validator: (_, value: string[] | undefined) => {
                  const entries = value || [];
                  const invalid = entries.filter((v) => !isValidOrigin(v));
                  if (invalid.length === 0) return Promise.resolve();
                  return Promise.reject(
                    new Error(
                      t('tenant.api.origins.invalid', {
                        values: invalid.join(', '),
                      }),
                    ),
                  );
                },
              },
            ]}
          >
            <Select
              disabled={disabled}
              loading={loading}
              mode="tags"
              placeholder="http://localhost:5173, https://example.com, https://*.example.com"
              suffixIcon={<GlobalOutlined />}
              open={false}
              tokenSeparators={[',', ' ']}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) =>
              prev.allowedOrigins !== curr.allowedOrigins
            }
          >
            {({ getFieldValue }) => {
              const origins: string[] = getFieldValue('allowedOrigins') || [];
              const hasWildcard = origins.some((o) => o?.trim() === '*');
              return hasWildcard ? (
                <Alert
                  type="warning"
                  showIcon
                  message={t('tenant.api.origins.wildcard.warning')}
                  style={{ marginTop: -16, marginBottom: 16 }}
                />
              ) : null;
            }}
          </Form.Item>
        </Col>
        <Col {...flexLayout.halfColumn}>
          <Form.Item
            name={['limits', 'monthlyRequests']}
            label={t('tenant.api.limit')}
            rules={[requiredField(t('tenant.api.limit'))]}
          >
            <InputNumber
              disabled={disabled}
              min={1}
              max={1_000_000}
              step={100}
              precision={0}
              style={{ width: '100%' }}
              prefix={<SafetyCertificateOutlined />}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[48, 0]} style={{ marginBottom: 24 }}>
        <Col {...flexLayout.fullColumn}>
          <Form.Item
            label={t('tenant.api.subscriptions.available')}
            rules={[requiredField(t('tenant.api.subscriptions.available'))]}
            style={{ marginBottom: 0 }}
          >
            <Space.Compact
              block
              style={{ width: '100%' }}
              orientation="horizontal"
            >
              <Form.Item name="subscriptionIds" noStyle>
                <Select
                  maxCount={isUnlimited ? undefined : 3}
                  disabled={disabled}
                  loading={loading}
                  mode="multiple"
                  placeholder={t(
                    'tenant.api.subscriptions.available.placeholder',
                  )}
                  onChange={() => setDirty(true)}
                  optionLabelProp="label"
                  options={subscriptions.map((sub) => ({
                    key: sub.id,
                    value: sub.id,
                    label: (
                      <div>
                        <strong>{sub.title}</strong>
                        <div
                          style={{ fontSize: '12px', color: colorTextTertiary }}
                        >
                          {sub.price?.originalPrice} {sub.price?.currency} /{' '}
                          {sub.price?.duration?.period}{' '}
                          {sub.price?.duration?.type}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>
              <Button
                style={{ height: 'auto' }}
                icon={<ReloadOutlined />}
                onClick={refetchSubscriptions}
              />
            </Space.Compact>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[48, 0]}>
        <Col span={24}>
          <Typography.Title level={5}>
            {t('tenant.api.keys.title')}
          </Typography.Title>
          <Table<IApiKey> {...tableProps} />
        </Col>
      </Row>
    </>
  );
};
